import React from "react"

interface TooltipProps {
  children: React.ReactNode,
  content: string,
  position?: "top" | "bottom" | "left" | "right",
  trigger?: "auto" | "hover" | "click",
  className?: string,
  id?: string,
}

interface TooltipState {
  isVisible: boolean,
  isTouchDevice: boolean
}

class TooltipInner extends React.Component<TooltipProps, TooltipState> {
  private tooltipRef: React.RefObject<HTMLDivElement>

  private tooltipId: string

  static defaultProps: Partial<TooltipProps> = {
    position: "top",
    trigger: "auto",
    className: "",
  }

  constructor(props: TooltipProps) {
    super(props)

    this.state = {
      isVisible: false,
      isTouchDevice: false,
    }

    this.tooltipRef = React.createRef()
    this.tooltipId = props.id || `tooltip-${Math.random().toString(36).substring(2, 11)}`

    this.handleClick = this.handleClick.bind(this)
    this.handleMouseEnter = this.handleMouseEnter.bind(this)
    this.handleMouseLeave = this.handleMouseLeave.bind(this)
    this.handleClickOutside = this.handleClickOutside.bind(this)
    this.handleKeyDown = this.handleKeyDown.bind(this)
    this.handleFocus = this.handleFocus.bind(this)
    this.handleBlur = this.handleBlur.bind(this)
    this.getPositionClasses = this.getPositionClasses.bind(this)
    this.getArrowClasses = this.getArrowClasses.bind(this)
    this.checkTouchDevice = this.checkTouchDevice.bind(this)
  }

  componentDidMount() {
    this.checkTouchDevice()
    document.addEventListener("mousedown", this.handleClickOutside)
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside)
  }

  checkTouchDevice() {
    const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0
    this.setState({ isTouchDevice })
  }

  handleClickOutside(event: MouseEvent) {
    if (this.tooltipRef.current && !this.tooltipRef.current.contains(event.target as Node)) {
      const { trigger } = this.props
      const { isTouchDevice } = this.state
      const shouldUseClick = trigger === "click" || (trigger === "auto" && isTouchDevice)

      if (shouldUseClick) {
        this.setState({ isVisible: false })
      }
    }
  }

  handleClick() {
    const { trigger } = this.props
    const { isTouchDevice } = this.state
    const shouldUseClick = trigger === "click" || (trigger === "auto" && isTouchDevice)

    if (shouldUseClick) {
      this.setState((prevState) => ({ isVisible: !prevState.isVisible }))
    }
  }

  handleMouseEnter() {
    const { trigger } = this.props
    const { isTouchDevice } = this.state
    const shouldUseHover = trigger === "hover" || (trigger === "auto" && !isTouchDevice)

    if (shouldUseHover) {
      this.setState({ isVisible: true })
    }
  }

  handleMouseLeave() {
    const { trigger } = this.props
    const { isTouchDevice } = this.state
    const shouldUseHover = trigger === "hover" || (trigger === "auto" && !isTouchDevice)

    if (shouldUseHover) {
      this.setState({ isVisible: false })
    }
  }

  handleFocus() {
    const { trigger } = this.props
    const { isTouchDevice } = this.state
    const shouldUseHover = trigger === "hover" || (trigger === "auto" && !isTouchDevice)

    if (shouldUseHover) {
      this.setState({ isVisible: true })
    }
  }

  handleBlur() {
    const { trigger } = this.props
    const { isTouchDevice } = this.state
    const shouldUseHover = trigger === "hover" || (trigger === "auto" && !isTouchDevice)

    if (shouldUseHover) {
      this.setState({ isVisible: false })
    }
  }

  handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Escape") {
      this.setState({ isVisible: false })
    }

    const { trigger } = this.props
    const { isTouchDevice } = this.state
    const shouldUseClick = trigger === "click" || (trigger === "auto" && isTouchDevice)

    if (shouldUseClick && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault()
      this.setState((prevState) => ({ isVisible: !prevState.isVisible }))
    }
  }

  getPositionClasses(): string {
    const { position } = this.props
    const positionMap = {
      top: "bottom-full left-1/2 transform -translate-x-1/2 mb-2",
      bottom: "top-full left-1/2 transform -translate-x-1/2 mt-2",
      left: "right-full top-1/2 transform -translate-y-1/2 mr-2",
      right: "left-full top-1/2 transform -translate-y-1/2 ml-2",
    }
    return positionMap[position || "top"]
  }

  getArrowClasses(): string {
    const { position } = this.props
    const arrowMap = {
      top: "top-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-white",
      bottom: "bottom-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-white",
      left: "left-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-white",
      right: "right-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-white",
    }
    return arrowMap[position || "top"]
  }

  render() {
    const { children, content, className } = this.props
    const { isVisible } = this.state

    return (
      <div
        ref={this.tooltipRef}
        className={`relative inline-block mx-auto max-w-fit mt-2 ${className || ""}`}
        onClick={this.handleClick}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        onKeyDown={this.handleKeyDown}
        {...(React.Children.toArray(children).some((child) => React.isValidElement(child)
          && ["button", "a", "input", "select", "textarea"].includes(child.type as string)) ? {} : { tabIndex: 0 })}
        aria-describedby={isVisible ? this.tooltipId : undefined}
      >
        {children}
        {isVisible && (
          <div
            id={this.tooltipId}
            role="tooltip"
            aria-hidden={!isVisible}
            className={`absolute z-50 px-5 py-3 text-xs text-black-secondary bg-white rounded-2xl shadow-lg whitespace-pre transition-opacity duration-200 ${this.getPositionClasses()}`}
          >
            {content}
            <div className={`absolute w-0 h-0 border-4 ${this.getArrowClasses()}`} />
          </div>
        )}
      </div>
    )
  }
}

export function Tooltip(props: TooltipProps) {
  return <TooltipInner {...props} />
}
